import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { DeleteIcon, DuplicateIcon, FilterTwoIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../../api/apis.config'
import { createOptionPrice } from '../../../../api/option-prices/create-option-price'
import { deleteOptionPrice } from '../../../../api/option-prices/delete-option-price'
import { duplicateOptionPrice } from '../../../../api/option-prices/duplicate-option-price'
import { updateOptionPrice } from '../../../../api/option-prices/update-option-price'
import CountryFlag from '../../../../components/elements/country-flag'
import { DynamicTableRef } from '../../../../components/elements/dynamic-table'
import NestedDataTable from '../../../../components/elements/nested-table'
import PeriodBadge from '../../../../components/elements/period-badge'
import OptionPriceForm from '../../../../components/pages/journey-places/option/option-prices/option-price-form'
import { Paths } from '../../../../lib/constants/path'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { OptionPrice } from '../../../../lib/types/models/option-prices/option-price'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { optionPriceDefaultValuesMapping } from '../../utils/option-price-default-values-mapping'

type Props = {
  id: number
}

const OptionPrices = ({ id }: Props) => {
  const tableRef = React.useRef<DynamicTableRef<OptionPrice>>(null)

  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'create' | 'duplicate' | 'delete' | 'edit',
    OptionPrice
  >(['create', 'duplicate', 'delete', 'edit'])

  const { isPending: loading, mutate: deleteOptionPriceMutation } = useMutation({
    mutationFn: deleteOptionPrice,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success("Prix d'option supprimé avec succès")
      closeModel('delete')
    }
  })
  const { mutateAsync } = useMutation({
    mutationFn: createOptionPrice,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success("Prix d'option créé avec succès")
    }
  })

  const { mutateAsync: duplicateOptionPriceMutate } = useMutation({
    mutationFn: duplicateOptionPrice,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success("Prix d'option dupliqué avec succès")
    }
  })

  const { mutateAsync: updateOptionPriceMutation } = useMutation({
    mutationFn: updateOptionPrice,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success("Prix d'option modifié avec succès")
    }
  })

  const { navigate } = useNavigator()

  const columns: Column<OptionPrice>[] = [
    {
      accessorKey: 'price',
      header: 'Prix',
      enableSorting: true,
      cell: (data) => `${data.row.original.price}€`
    },
    {
      accessorKey: 'formulas',
      header: 'Formules',
      enableSorting: false,
      cell: (data) =>
        data.row.original.formulas?.length > 0 ? (
          <div className="flex gap-2 items-center flex-wrap w-52 text-wrap">
            {data.row.original.formulas?.map((formula) => getTranslatedName(formula)).join(', ')}
          </div>
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'periods',
      header: 'Périodes',
      enableSorting: false,
      cell: (data) => {
        const periods = data.row.original.periods
        return periods?.length > 0 ? (
          <div className="flex gap-2 items-center flex-wrap py-2">
            {periods?.map((period) => (
              <PeriodBadge key={period.id} period={period} />
            ))}
          </div>
        ) : (
          '-'
        )
      }
    },
    {
      accessorKey: 'countries',
      header: 'Pays',
      enableSorting: false,
      cell: (data) => {
        const countries = data.row.original.countries

        return countries?.length > 0 ? (
          <div className="flex gap-1 flex-wrap">
            {countries?.map((country) => (
              <CountryFlag key={country.code} code={country.code} />
            ))}
          </div>
        ) : (
          '-'
        )
      }
    },
    {
      accessorKey: 'minAge',
      header: 'Âge min',
      enableSorting: true,
      cell: (data) => data.row.original.minAge
    },
    {
      accessorKey: 'maxAge',
      header: 'Âge max',
      enableSorting: true,
      cell: (data) => data.row.original.maxAge
    },
    {
      accessorKey: 'appliedDurationsInDays',
      header: 'Durée en jours',
      enableSorting: false,
      cell: (data) => {
        const duration = data.row.original.appliedDurationsInDays
        return duration?.length > 0 ? (
          <div className="flex gap-2 items-center flex-wrap w-28 py-2">
            {duration?.map((duration, index) => (
              <Badge
                key={duration + index}
                variant="grey"
                textColor={tailwindColors.grey.DEFAULT}
                text={duration?.toString()}
              />
            ))}
          </div>
        ) : (
          '-'
        )
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Créé',
      enableSorting: true,
      cell: (data) => (
        <Badge
          variant="grey"
          textColor={tailwindColors.grey.DEFAULT}
          text={DateFormatter.DDMMYYYY(data.row.original.createdAt)}
        />
      )
    },
    {
      accessorKey: 'updatedAt',
      header: 'Mise à jour',
      enableSorting: true,
      cell: (data) => (
        <Badge
          variant="grey"
          textColor={tailwindColors.grey.DEFAULT}
          text={DateFormatter.DDMMYYYY(data.row.original.updatedAt)}
        />
      )
    },
    {
      enableSorting: false,
      header: '',
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <ActionButton
            icon={DuplicateIcon}
            variant="primary"
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('duplicate', row.original)
            }}
          />
          <ActionButton
            icon={DeleteIcon}
            variant={'danger'}
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <>
      <NestedDataTable<OptionPrice, { optionId: number }>
        ref={tableRef}
        title={"Prix de l'option"}
        deleteMessage={"Êtes-vous sûr de vouloir supprimer le prix de l'option ?"}
        columns={columns}
        onRowClick={(optionPrice) => toggleModal('edit', optionPrice)}
        filters={{ optionId: id }}
        isSearchable={false}
        endpoint={API.optionPrices.getAll.url}
        permissions={(_) => ({
          delete: false
        })}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: "Ajouter un prix d'option",
            fn: () => toggleModal('create')
          }
        ]}
      />
      <Modal
        isOpen={modalState.create}
        title={"Créer le prix d'option"}
        icon={FilterTwoIcon}
        hideActions
        size="medium"
        onCancel={() => {
          closeModel('create')
          navigate(Paths.JOURNEY_PLACE_OPTION_UPDATE_PAGE.replace(':id', id.toString()))
        }}
      >
        <OptionPriceForm
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              await mutateAsync({ ...values, optionId: id })
              closeModel('create')
            },
            onCancel: () => closeModel('create')
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState.duplicate}
        title={"Dupliquer le prix de l'option"}
        icon={FilterTwoIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('duplicate')}
      >
        <OptionPriceForm
          defaultValues={{ ...optionPriceDefaultValuesMapping(selectedData), price: undefined }}
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              if (!selectedData) return
              await duplicateOptionPriceMutate({ ...values, optionId: id, id: selectedData.id })
              closeModel('duplicate')
            },
            onCancel: () => closeModel('duplicate')
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState.edit}
        title={"Modifier le prix de l'option"}
        icon={FilterTwoIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('edit')}
      >
        <OptionPriceForm
          defaultValues={optionPriceDefaultValuesMapping(selectedData)}
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              if (!selectedData) return
              await updateOptionPriceMutation({
                ...values,
                id: selectedData.id,
                optionId: id
              })
              closeModel('edit')
            },
            onCancel: () => closeModel('edit')
          })}
        />
      </Modal>

      <Modal
        actionButtonsOptions={{
          confirmText: 'Supprimer',
          cancelText: 'Fermer',
          onConfirm: () => {
            if (!selectedData) return
            deleteOptionPriceMutation([selectedData.id])
          }
        }}
        onCancel={() => closeModel('delete')}
        isOpen={modalState.delete}
        title={'Voulez-vous vraiment supprimer ces prix d’option ?'}
        icon={DeleteIcon}
        isLoading={loading}
        size="medium"
        variant={'destructive'}
      />
    </>
  )
}

export default OptionPrices
